/**
 * PublicChatWidget — floating live chat bubble for public-facing pages.
 *
 * - Shown only on pages that are NOT under /portal, /worker or /workforce.
 * - Visitor enters name, optional email, and a message to start.
 * - The backend stores the chat with source="public" in the same support_chats
 *   collection used by worker chats, so the admin Support page shows both.
 * - Replies from admin appear within ~8 seconds (polling).
 * - When admin replies and contact_email was provided, the visitor also gets an email.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import API_BASE from '../../utils/apiBase';

// Storage keys
const STORAGE_KEY_CHAT = 'osabea_pub_chat';

const POLL_INTERVAL_MS = 8000;

// Minimal inline styles — no Tailwind dependency here so the widget works
// on public pages even if the design system is not fully loaded.
const S = {
  bubble: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '12px',
    fontFamily: "'Inter', sans-serif",
  },
  panel: {
    width: '340px',
    maxWidth: 'calc(100vw - 32px)',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    overflow: 'hidden',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '520px',
  },
  header: {
    background: '#0F5C5E',
    color: '#fff',
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '14px',
    fontWeight: '600',
  },
  headerTitle: { display: 'flex', alignItems: 'center', gap: '8px' },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '18px',
    lineHeight: 1,
    padding: '0 2px',
    opacity: 0.8,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    fontSize: '13px',
  },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '12px', fontWeight: '500', color: '#444' },
  input: {
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '13px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  textarea: {
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '13px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: '72px',
  },
  submitBtn: {
    background: '#0F5C5E',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
  },
  submitBtnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  msgRow: (fromAdmin) => ({
    display: 'flex',
    justifyContent: fromAdmin ? 'flex-start' : 'flex-end',
  }),
  msgBubble: (fromAdmin) => ({
    maxWidth: '82%',
    borderRadius: fromAdmin ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
    padding: '8px 12px',
    background: fromAdmin ? '#f3f4f6' : '#0F5C5E',
    color: fromAdmin ? '#111' : '#fff',
    fontSize: '13px',
    lineHeight: '1.4',
  }),
  msgTime: { fontSize: '10px', opacity: 0.6, marginTop: '3px', textAlign: 'right' },
  replyArea: {
    borderTop: '1px solid #e5e7eb',
    padding: '10px 12px',
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-end',
  },
  replyInput: {
    flex: 1,
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '13px',
    outline: 'none',
    resize: 'none',
    minHeight: '36px',
    maxHeight: '96px',
  },
  sendBtn: {
    background: '#0F5C5E',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 14px',
    fontSize: '13px',
    cursor: 'pointer',
    flexShrink: 0,
    fontWeight: '600',
  },
  onlineDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#4ade80',
    display: 'inline-block',
  },
  fab: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    background: '#0F5C5E',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
    fontSize: '22px',
    transition: 'transform 0.2s',
  },
  errorBanner: {
    background: '#fef2f2',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '12px',
    color: '#b91c1c',
  },
};

function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export default function PublicChatWidget() {
  const location = useLocation();

  // Don't render on authenticated or tenant workforce entry pages.
  const path = location.pathname;
  if (
    path === '/'
    || path.startsWith('/portal')
    || path.startsWith('/worker')
    || path.startsWith('/workforce')
  ) return null;

  return <ChatBubble />;
}

function ChatBubble() {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_CHAT);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState('open');
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [prevMsgCount, setPrevMsgCount] = useState(0);
  const [unread, setUnread] = useState(0);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const API = `${API_BASE.replace(/\/api$/, '')}/api`;

  const persistSession = useCallback((s) => {
    setSession(s);
    if (s) {
      localStorage.setItem(STORAGE_KEY_CHAT, JSON.stringify(s));
    } else {
      localStorage.removeItem(STORAGE_KEY_CHAT);
    }
  }, []);

  const fetchMessages = useCallback(async (chatId, token) => {
    try {
      const res = await fetch(
        `${API}/public/support-chat/${chatId}?token=${encodeURIComponent(token)}`
      );
      if (!res.ok) return;
      const data = await res.json();
      setMessages(data.messages || []);
      setStatus(data.status || 'open');
    } catch {
      /* silent */
    }
  }, [API]);

  // Poll for new messages while the session exists
  useEffect(() => {
    if (!session) return;
    fetchMessages(session.chat_id, session.visitor_token);
    pollRef.current = setInterval(
      () => fetchMessages(session.chat_id, session.visitor_token),
      POLL_INTERVAL_MS
    );
    return () => clearInterval(pollRef.current);
  }, [session, fetchMessages]);

  // Track unread when panel is closed
  useEffect(() => {
    if (!open && messages.length > prevMsgCount) {
      setUnread((u) => u + (messages.length - prevMsgCount));
    }
    setPrevMsgCount(messages.length);
  }, [messages, open, prevMsgCount]);

  // Clear unread on open
  useEffect(() => {
    if (open) setUnread(0);
  }, [open]);

  // Auto-scroll
  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  const handleFormChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const startChat = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.message.trim()) {
      setError('Please enter your name and message.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/public/support-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          contact_email: form.email.trim() || undefined,
          message: form.message.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to start chat');
      persistSession({ chat_id: data.chat_id, visitor_token: data.visitor_token });
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const sendMessage = async () => {
    const text = reply.trim();
    if (!text || !session || status !== 'open') return;
    setSending(true);
    try {
      const res = await fetch(
        `${API}/public/support-chat/${session.chat_id}/message?token=${encodeURIComponent(
          session.visitor_token
        )}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: text }),
        }
      );
      if (res.ok) {
        setReply('');
        await fetchMessages(session.chat_id, session.visitor_token);
      }
    } catch {
      /* silent */
    } finally {
      setSending(false);
    }
  };

  const resetChat = () => {
    persistSession(null);
    setMessages([]);
    setStatus('open');
    setUnread(0);
    clearInterval(pollRef.current);
  };

  return (
    <div style={S.bubble}>
      {open && (
        <div style={S.panel}>
          {/* Header */}
          <div style={S.header}>
            <div style={S.headerTitle}>
              <span style={S.onlineDot} />
              Osabea Support
            </div>
            <button
              style={S.closeBtn}
              onClick={() => setOpen(false)}
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          {/* Body */}
          {!session ? (
            // Start-chat form
            <form style={S.body} onSubmit={startChat} noValidate>
              <p style={{ fontSize: '13px', color: '#555', margin: 0 }}>
                Hi there! Ask us anything about care services, staffing, or how Osabea
                can help you. We typically reply within a few minutes.
              </p>
              {error && <div style={S.errorBanner}>{error}</div>}
              <div style={S.formGroup}>
                <label style={S.label}>Your name *</label>
                <input
                  style={S.input}
                  value={form.name}
                  onChange={handleFormChange('name')}
                  placeholder="e.g. Sarah Jones"
                  required
                />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Email (optional — to receive our reply by email)</label>
                <input
                  style={S.input}
                  type="email"
                  value={form.email}
                  onChange={handleFormChange('email')}
                  placeholder="you@example.com"
                />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Message *</label>
                <textarea
                  style={S.textarea}
                  value={form.message}
                  onChange={handleFormChange('message')}
                  placeholder="How can we help you today?"
                  required
                />
              </div>
              {/* Honeypot */}
              <input
                type="text"
                name="website"
                style={{ display: 'none' }}
                tabIndex={-1}
                autoComplete="off"
              />
              <button
                type="submit"
                style={{
                  ...S.submitBtn,
                  ...(submitting ? S.submitBtnDisabled : {}),
                }}
                disabled={submitting}
              >
                {submitting ? 'Starting chat…' : 'Start Chat'}
              </button>
            </form>
          ) : (
            // Thread view
            <>
              <div style={S.body}>
                {messages.length === 0 && (
                  <p style={{ color: '#888', fontSize: '12px', textAlign: 'center', margin: 'auto 0' }}>
                    Your message has been sent. We'll reply shortly.
                  </p>
                )}
                {messages.map((m, i) => {
                  const fromAdmin = m.from === 'admin';
                  return (
                    <div key={m.id || i} style={S.msgRow(fromAdmin)}>
                      <div style={S.msgBubble(fromAdmin)}>
                        {fromAdmin && (
                          <div style={{ fontSize: '10px', opacity: 0.65, marginBottom: '2px' }}>
                            Osabea Support
                          </div>
                        )}
                        {m.text}
                        <div style={S.msgTime}>{fmtTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                {status === 'closed' && (
                  <p style={{ color: '#888', fontSize: '11px', textAlign: 'center' }}>
                    This chat has been closed.{' '}
                    <button
                      onClick={resetChat}
                      style={{ color: '#0F5C5E', background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', textDecoration: 'underline' }}
                    >
                      Start a new chat
                    </button>
                  </p>
                )}
                <div ref={messagesEndRef} />
              </div>

              {status === 'open' && (
                <div style={S.replyArea}>
                  <textarea
                    style={S.replyInput}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Type a message…"
                    rows={1}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    disabled={sending}
                  />
                  <button
                    style={S.sendBtn}
                    onClick={sendMessage}
                    disabled={sending || !reply.trim()}
                  >
                    Send
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Floating action button */}
      <button
        style={S.fab}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close chat' : 'Open chat'}
        title="Chat with us"
      >
        {open ? '×' : '💬'}
        {!open && unread > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              background: '#ef4444',
              color: '#fff',
              borderRadius: '50%',
              width: '18px',
              height: '18px',
              fontSize: '11px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '700',
            }}
          >
            {unread}
          </span>
        )}
      </button>
    </div>
  );
}
